import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SARAH_PROVIDERS } from "@alm/validation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { DisconnectProviderDialog } from "./-components/disconnect-provider-dialog";
import { ProviderAccountDialog } from "./-components/provider-account-dialog";
import { ProviderCard } from "./-components/provider-card";
import type { CredentialTarget, ProviderAccount } from "./-components/provider-accounts";
import { NeedsActivation } from "@/components/app/activation/needs-activation";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/credentials")({
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-org-settings"),
  component: Credentials,
});

const META = "Twilio carries the phone line; xAI is the agent. Both accounts are yours, not ours.";

/** Bring-your-own-key. A building's own row that is off or failed falls back to the organization's
 * rather than taking the line down. Secrets are write-only: shown as set or not set, never masked,
 * because a mask leaks length. */
function Credentials() {
  const { facilityId } = useFacility();
  /** Credentials spend the operator's money, so they sit behind the settings
   *  permission rather than the roster one. This hides; the API refuses. */
  const canManage = useCan("settings:manage");
  const realFacility = facilityId === "all" ? null : facilityId;

  const [editing, setEditing] = useState<CredentialTarget | null>(null);
  const [disconnecting, setDisconnecting] = useState<{
    row: ProviderAccount;
    describes: string;
  } | null>(null);

  /* The read gate is computed here, not returned early: an early return between hooks throws
   * "rendered fewer hooks than expected" when a query resolves. */
  const accounts = useQuery({
    ...orpc.frontDesk.staff.providers.queryOptions(),
    enabled: canManage,
  });
  const rows = accounts.data ?? [];
  const facilityName =
    rows.find((row) => row.facilityId === realFacility)?.facilityName ?? "This building";

  return (
    <>
      <PageHeader title="Front desk credentials" meta={META} />

      {!canManage ? (
        <EmptyState
          title="You do not have access to these credentials"
          description="Provider keys are managed by an administrator. Ask one of yours to connect or change an account."
        />
      ) : accounts.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : accounts.isError ? (
        <ReadFailed
          message="Could not load these credentials."
          error={accounts.error}
          onRetry={() => void accounts.refetch()}
          retrying={accounts.isFetching}
        />
      ) : (
        <div className="space-y-4">
          {rows.length === 0 ? (
            <NeedsActivation featureKey="front_desk" facilityId={realFacility} />
          ) : null}
          {SARAH_PROVIDERS.map((provider) => (
            <ProviderCard
              key={provider}
              provider={provider}
              rows={rows}
              facilityId={realFacility}
              facilityName={facilityName}
              canManage={canManage}
              onEdit={setEditing}
              onDisconnect={setDisconnecting}
            />
          ))}
        </div>
      )}

      {editing ? (
        <ProviderAccountDialog
          key={`${editing.provider}:${editing.scope}:${editing.row?.id ?? "new"}`}
          target={editing}
          facilityId={realFacility}
          facilityName={facilityName}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <DisconnectProviderDialog target={disconnecting} onClose={() => setDisconnecting(null)} />
    </>
  );
}
